import { useRef } from "react";
import { motion } from "framer-motion";
import { Volume2 } from "lucide-react";
import { ShareButton } from "@/components/ShareButton";
import { useYouTubeAutoplay } from "@/lib/youtube";

const embedSrc = (entry) => {
  const params = [
    entry.si && `si=${entry.si}`,
    entry.start > 0 && `start=${entry.start}`,
    "autoplay=1",
    "mute=1",
    "playsinline=1",
    "rel=0",
    "enablejsapi=1",
    `origin=${encodeURIComponent(window.location.origin)}`,
  ].filter(Boolean);
  return `https://www.youtube-nocookie.com/embed/${entry.video_id}?${params.join("&")}`;
};

export const VideoAnswer = ({ entry }) => {
  const iframeRef = useRef(null);
  const { mutedFallback, unmute } = useYouTubeAutoplay(iframeRef, entry.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="space-y-3"
      data-testid="video-answer"
    >
      <div className="overflow-hidden rounded-2xl border-4 border-amber-400 bg-black shadow-[0_0_50px_rgba(225,29,72,0.4)]">
        <div className="relative aspect-video w-full">
          <iframe
            ref={iframeRef}
            data-testid="youtube-video-player"
            className="absolute inset-0 h-full w-full"
            src={embedSrc(entry)}
            title="BollyGPT answer"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
          {mutedFallback && (
            <button
              onClick={unmute}
              data-testid="tap-for-sound-button"
              className="absolute left-1/2 top-4 z-10 flex -translate-x-1/2 animate-pulse items-center gap-2 rounded-full bg-pink-500 px-5 py-2.5 text-sm font-bold text-white shadow-[0_0_30px_rgba(236,72,153,0.6)]"
            >
              <Volume2 className="h-5 w-5" /> Tap for sound
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ShareButton entry={entry} />
        <a href={`https://www.youtube.com/watch?v=${entry.video_id}${entry.start > 0 ? `&t=${entry.start}s` : ""}`} target="_blank" rel="noreferrer" data-testid="watch-on-youtube-link" className="text-xs text-slate-400 hover:text-amber-300">Not playing? Watch on YouTube</a>
      </div>
    </motion.div>
  );
};
